import type { ReadonlyJSONValue } from "@rocicorp/zero";
import { type ReactNode, useState } from "react";
import type { ChatEntryWithSubTables } from "../../../shared/aiSdkMessages";
import { resolveStepColor } from "../../../shared/colors";
import { displayStepConfig } from "../../../shared/stepConfig";
import {
  SUB_AGENT_WAIT_TOOLS,
  type SubAgentResult,
  type SubAgentStatus,
} from "../../../shared/subAgent";
import { queries } from "../../../shared/queries";
import { useQuery } from "@rocicorp/zero/react";
import { useStreamingText } from "../../hooks/useStreamingText";
import { branchRunChat } from "../../lib/branchRunChat";
import { ActivityAnimationContext } from "../ActivityAnimationContext";
import { ToolStatus } from "../Chat/ToolStatus";
import { ExpandedContent } from "../Chat/Expand";
import { FallbackHeader } from "../Chat/ToolUse";
import { ActivityEntry } from "../Chat/ToolUseGroup";
import type { ChatTool, ToolUseProps } from "../Chat/ChatTool";
import type { BashData } from "../../tools/Bash";
import { connectToAppActivity } from "../../tools/ConnectToApp";
import {
  PushLiveBody,
  PushLiveHeader,
  parseSummary,
  pushLiveActivity,
} from "../../tools/PushLive";
import type { ChatProps } from "../Chat/chatTypes";
import {
  askQuestion,
  approvePlan,
  bash,
  copyStorageFileToStep,
  editFile,
  execute,
  getRunExecutions,
  getStepLastExecution,
  getStepTest,
  listVersions,
  pinStepTest,
  readFile,
  readSkillFile,
  readStorageFile,
  readVersionChat,
  runSkillScript,
  runStep,
  runStepTests,
  seedBranchStorage,
  skillTool,
  unpinStepTest,
  webFetch,
  webpageDebugActivity,
  webSearch,
  writeFile,
} from "../../tools";

const childTools = (stepColors: BashData): ReadonlyMap<string, ChatTool> =>
  new Map(
    [
      askQuestion,
      approvePlan,
      bash(stepColors),
      connectToAppActivity("connectChatToApp"),
      connectToAppActivity("connectStepToApp"),
      copyStorageFileToStep(stepColors),
      editFile(stepColors),
      execute,
      getRunExecutions,
      getStepLastExecution,
      getStepTest,
      listVersions,
      pinStepTest,
      pushLiveActivity,
      readFile(stepColors),
      readSkillFile,
      readStorageFile,
      readVersionChat,
      runSkillScript,
      runStep,
      runStepTests,
      seedBranchStorage,
      skillTool,
      unpinStepTest,
      webFetch,
      ...webpageDebugActivity,
      webSearch,
      writeFile(stepColors),
    ].map((tool) => [tool.name, tool])
  );

type ChildChat = {
  id: string;
  respondingAt: number | null;
  lastLoopHeartbeatAt: number | null;
  entries: ChatProps["entries"];
};

type Agent = { workflowId: string; branchId: string | null };

function childEntries(
  chats: readonly ChildChat[],
  from: number,
  until: number | null
) {
  return [
    ...new Map(
      chats
        .flatMap((chat) => chat.entries as ChatEntryWithSubTables[])
        .filter(
          (entry) =>
            entry.createdAt >= from &&
            (until == null || entry.createdAt <= until) &&
            (entry.entryType === "AssistantMessage" ||
              entry.assistantReasoning ||
              entry.toolCall)
        )
        .map((entry) => [entry.id, entry])
    ).values(),
  ].sort((a, b) => a.createdAt - b.createdAt || a.id.localeCompare(b.id));
}

// A run keeps the chat it started in after the agent saves new commits, so the newest chat may not be running.
function runChat(chats: readonly ChildChat[]) {
  return branchRunChat(
    chats.at(-1),
    chats.map((chat) => ({ chats: chat }))
  );
}

// Tool input reaches the database only once the model finishes writing it, so live rows read the child's stream.
function useChildStream(chats: readonly ChildChat[], live: boolean) {
  const chat = runChat(chats);
  return useStreamingText(
    chat?.id ?? null,
    live && chat?.respondingAt != null && chat.lastLoopHeartbeatAt != null
  ).messages;
}

function ChildActivity({
  chats,
  tools,
  from,
  until,
  expanded,
  fallback,
}: {
  chats: readonly ChildChat[];
  tools: ReadonlyMap<string, ChatTool>;
  from: number;
  until: number | null;
  expanded: boolean;
  fallback?: ReactNode;
}) {
  const [expandedToolUse, setExpandedToolUse] = useState<string | null>(null);
  const responding = runChat(chats)?.respondingAt != null;
  const streams = useChildStream(chats, expanded && until == null);
  const entries = childEntries(chats, from, until);
  if (entries.length === 0) return fallback;
  return entries.map((entry, index) => (
    <ActivityEntry
      key={entry.id}
      entry={entry}
      live={until == null && responding && index === entries.length - 1}
      connected
      hasNext={index < entries.length - 1}
      stream={streams.get(entry.id)}
      interrupted={!responding && entry.toolCall?.completedAt == null}
      expandedToolUse={expandedToolUse}
      setExpandedToolUse={setExpandedToolUse}
      tools={tools}
    />
  ));
}

function CurrentChildActivity({
  chats,
  tools,
  from,
  text,
}: {
  chats: readonly ChildChat[];
  tools: ReadonlyMap<string, ChatTool>;
  from: number;
  text: string;
}) {
  const streams = useChildStream(chats, true);
  const current = childEntries(chats, from, null).findLast(
    (entry) => entry.toolCall
  );
  const call = current?.toolCall;
  if (!current || !call)
    return <ToolStatus icon="3b-mark" text={text} pending />;
  const tool = tools.get(call.toolName);
  const Header = tool?.Header ?? FallbackHeader;
  return (
    <ActivityAnimationContext
      value={{
        live: true,
        activityId: `${current.id}:${call.completedAt ?? "pending"}`,
      }}
    >
      <Header
        toolUse={{
          id: current.id,
          toolName: call.toolName,
          input: streams.get(current.id)?.toolInput ?? call.input,
          output: call.output,
          isError: call.isError,
        }}
        result={
          call.completedAt != null
            ? { id: current.id, output: call.output, isError: !!call.isError }
            : null
        }
        createdAt={current.createdAt}
        data={tool?.data}
        interrupted={false}
      />
    </ActivityAnimationContext>
  );
}

type RenderChildChats = (
  chats: readonly ChildChat[],
  tools: ReadonlyMap<string, ChatTool>
) => ReactNode;

function BranchChats({
  branchId,
  tools,
  render,
}: {
  branchId: string;
  tools: ReadonlyMap<string, ChatTool>;
  render: RenderChildChats;
}) {
  const [commits] = useQuery(queries.loadBranchChats({ branchId }));
  return render(
    commits.flatMap((commit) => (commit.chats ? [commit.chats] : [])),
    tools
  );
}

function LiveChats({
  workflowId,
  tools,
  render,
}: {
  workflowId: string;
  tools: ReadonlyMap<string, ChatTool>;
  render: RenderChildChats;
}) {
  const [live] = useQuery(queries.loadLiveChat({ workflowId }));
  return render(live?.chat ? [live.chat] : [], tools);
}

function AgentChats({
  agent,
  render,
}: {
  agent: Agent;
  render: RenderChildChats;
}) {
  const [workflow] = useQuery(
    queries.workflowBranchRoutes({
      workflowId: agent.workflowId,
      branchId: agent.branchId ?? undefined,
    })
  );
  const stepColors = new Map(
    (workflow?.branches?.headCommit?.files ?? []).flatMap((file) => {
      const dir = file.path.split("/")[0]!;
      return file.blob
        ? [
            [
              dir,
              resolveStepColor(
                displayStepConfig(file.blob.content).color ?? "",
                dir
              ),
            ] as const,
          ]
        : [];
    })
  );
  const tools = childTools({ getStepColor: (name) => stepColors.get(name) });
  return agent.branchId ? (
    <BranchChats branchId={agent.branchId} tools={tools} render={render} />
  ) : (
    <LiveChats workflowId={agent.workflowId} tools={tools} render={render} />
  );
}

function WorkingLine({
  agent,
  from,
  text,
}: {
  agent: Agent;
  from: number;
  text: string;
}) {
  return (
    <AgentChats
      agent={agent}
      render={(chats, tools) => (
        <CurrentChildActivity
          chats={chats}
          tools={tools}
          from={from}
          text={text}
        />
      )}
    />
  );
}

function AgentActivity({
  agent,
  from,
  until,
  expanded,
  fallback,
}: {
  agent: Agent;
  from: number;
  until: number | null;
  expanded: boolean;
  fallback?: ReactNode;
}) {
  return (
    <AgentChats
      agent={agent}
      render={(chats, tools) => (
        <ChildActivity
          chats={chats}
          tools={tools}
          from={from}
          until={until}
          expanded={expanded}
          fallback={fallback}
        />
      )}
    />
  );
}

function useToolAgent({ toolUse, result, data, interrupted }: ToolUseProps) {
  const [agents] = useQuery(
    queries.subAgentsForChat({ chatId: data as string })
  );
  const input = toolUse.input as { workflowId?: string };
  const output = result?.output as
    | Partial<SubAgentResult>
    | { status: "running"; workflowId: string }
    | undefined;
  const agent =
    agents.find((agent) => agent.awaitingEntryId === toolUse.id) ??
    agents.find(
      (agent) => agent.workflowId === (output?.workflowId ?? input.workflowId)
    );
  // A completed call reports "running" when it steered an agent whose earlier call receives the result.
  const status: SubAgentStatus | "sent" = result
    ? output?.status === "running"
      ? "sent"
      : (output?.status ?? (result.isError ? "failed" : "idle"))
    : agent?.awaitingEntryId === toolUse.id
      ? agent.status
      : interrupted
        ? "stopped"
        : "running";
  return { agent, status };
}

function pushLiveAnswer(
  result: ToolUseProps["result"],
  status: SubAgentStatus | "sent"
) {
  if (status !== "idle") return null;
  const { answered } = (result?.output ?? {}) as {
    answered?: {
      tool: string;
      entryId: string;
      input?: ReadonlyJSONValue;
      output?: ReadonlyJSONValue;
    };
  };
  return answered?.tool === "pushLive" && answered.output
    ? { ...answered, output: answered.output }
    : null;
}

function SubAgentToolHeader(props: ToolUseProps) {
  const { agent, status } = useToolAgent(props);
  const input = props.toolUse.input as {
    name?: string;
    activeDescription?: string;
    completedDescription?: string;
    failedDescription?: string;
  };
  const name = agent?.workflow?.name ?? input.name ?? "workflow";
  const text = {
    running: input.activeDescription
      ? `${input.activeDescription}…`
      : `Working on ${name}…`,
    needs_input: `${name} needs input`,
    idle: input.completedDescription ?? `Worked on ${name}`,
    failed: input.failedDescription ?? `${name} failed`,
    stopped: `Stopped ${name}`,
    sent: `Sent a message to ${name}`,
  }[status];
  // Expanded, the activity below already ends with the current step.
  if (
    agent &&
    (status === "running" || status === "needs_input") &&
    !props.interrupted &&
    !props.expanded
  )
    return <WorkingLine agent={agent} from={props.createdAt} text={text} />;
  const answer = pushLiveAnswer(props.result, status);
  if (answer)
    return (
      <PushLiveHeader
        {...props}
        result={{ id: answer.entryId, output: answer.output, isError: false }}
      />
    );
  return (
    <ToolStatus
      icon="3b-mark"
      text={text}
      pending={status === "running" && !props.interrupted}
    />
  );
}

function SubAgentToolBody(props: ToolUseProps) {
  const { agent, status } = useToolAgent(props);
  const answer = pushLiveAnswer(props.result, status);
  if (answer && (answer.output as { pushed?: unknown }).pushed === true) {
    if (!parseSummary(answer.input)) return null;
    return (
      <ExpandedContent>
        <PushLiveBody
          {...props}
          toolUse={{ ...props.toolUse, input: answer.input ?? null }}
        />
      </ExpandedContent>
    );
  }
  const output = props.result?.output as Partial<SubAgentResult> | undefined;
  const conversation = output?.workflowId
    ? { workflowId: output.workflowId, branchId: output.branchId ?? null }
    : agent;
  if (!conversation) return null;
  const summary = output && "summary" in output ? output.summary : undefined;
  return (
    <AgentActivity
      agent={conversation}
      from={props.createdAt}
      until={props.result ? (props.completedAt ?? null) : null}
      expanded={!!props.expanded}
      fallback={summary ? <ExpandedContent>{summary}</ExpandedContent> : null}
    />
  );
}

export function subAgentTools(chatId: string): ChatTool[] {
  return [...SUB_AGENT_WAIT_TOOLS].map((name) => ({
    name,
    Header: SubAgentToolHeader,
    Body: SubAgentToolBody,
    data: chatId,
    expandableWhilePending: true,
    alwaysLoadOutput: true,
  }));
}
